import type {
  ActivityEvent,
  AdminQuestion,
  AdminUser,
  AmaQuestion,
  ApiResponse,
  AuditEntry,
  CampaignArticle,
  Candidacy,
  CandidacyCreated,
  CandidacyDetail,
  CandidacyOffice,
  CandidacyOrigin,
  CandidacyReview,
  CountryLite,
  ElectionSummary,
  Forecast,
  GenericPoll,
  Leaderboard,
  PollReview,
  PollTally,
  RaceCandidacies,
  UserProfile,
  WorldElection,
} from "@shared/types";
import type { ForecastStatus } from "@shared/warm-progress";
import { adminAuth } from "./adminAuth";
import { editToken } from "./editToken";
import { ApiError } from "./apiError";

/** Header that folds the per-user token into poll dedup (1 token = 1 vote) when
 * this device holds one. Poll votes only — never an edit-auth call. */
function voteHeaders(): Record<string, string> {
  const t = editToken.getUser();
  return t ? { "x-edit-token": t } : {};
}

interface ReqOpts {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

async function request<T>(url: string, opts: ReqOpts = {}): Promise<T> {
  const init: RequestInit = { method: opts.method ?? "GET", headers: { ...opts.headers } };
  if (opts.body !== undefined) {
    init.headers = { "Content-Type": "application/json", ...init.headers };
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(url, init);
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.success) {
    throw new ApiError(json.error, { code: json.code, requiredLevel: json.requiredLevel, currentLevel: json.currentLevel });
  }
  return json.data;
}

const get = <T>(url: string) => request<T>(url);
const post = <T>(url: string, body: unknown, headers?: Record<string, string>) =>
  request<T>(url, { method: "POST", body, headers });

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** One forecast fetch. 200 → ready (the Forecast); 202 → still warming (a status
 * to drive the loader). The election id is the job id — poll the same URL. */
export type ForecastPoll = { ready: true; data: Forecast } | { ready: false; status: ForecastStatus };

async function forecastPollOnce(id: string): Promise<ForecastPoll> {
  const res = await fetch(`/api/elections/${id}/forecast`);
  const json = (await res.json()) as ApiResponse<Forecast | ForecastStatus>;
  if (!json.success) throw new Error(json.error);
  if (res.status === 202) return { ready: false, status: json.data as ForecastStatus };
  return { ready: true, data: json.data as Forecast };
}

/** Imperative poll-until-ready, for callers that just want the Forecast (e.g.
 * the post-vote refetch). Bounded so it can't spin forever. */
async function getForecastReady(id: string, tries = 40): Promise<Forecast> {
  for (let i = 0; i < tries; i++) {
    const r = await forecastPollOnce(id);
    if (r.ready) return r.data;
    await sleep(800);
  }
  throw new Error("warming");
}


/** Rich fields are tiptap JSON strings (built by the editor). */
export interface DeclareBody {
  username: string;
  party?: string;
  introDoc?: string;
  measures?: { title: string; detailDoc?: string | null }[];
}

export interface PlatformBody {
  introDoc?: string;
  party?: string;
  measures: { title: string; detailDoc?: string | null }[];
}

export const api = {
  countries: () => get<CountryLite[]>("/api/countries"),
  countryElections: (id: string) => get<ElectionSummary[]>(`/api/countries/${id}/elections`),
  world: (phase?: "live" | "soon" | "done") =>
    get<WorldElection[]>(`/api/world/elections${phase ? `?phase=${phase}` : ""}`),
  activity: (countryId: string) => get<ActivityEvent[]>(`/api/countries/${countryId}/activity`),

  // --- Community polls ---
  // The per-user token rides along so the server can flag polls this device owns
  // (`closeable`) and, on create, stamp the verified creator.
  polls: (opts?: { country?: string | null; scope?: "country" | "global" | "all" }) => {
    const qs = new URLSearchParams();
    if (opts?.country) qs.set("country", opts.country);
    if (opts?.scope) qs.set("scope", opts.scope);
    const q = qs.toString();
    return request<GenericPoll[]>(`/api/polls${q ? `?${q}` : ""}`, { headers: voteHeaders() });
  },
  poll: (id: string) => request<GenericPoll>(`/api/polls/${id}`, { headers: voteHeaders() }),
  createPoll: (body: {
    question: string;
    options: string[];
    countryId?: string | null;
    tag?: string;
    requireVerified?: boolean;
    minLevel?: number | null;
    endDate?: number | null;
  }) => post<GenericPoll>("/api/polls", body, voteHeaders()),
  votePoll: (id: string, optionId: string) => post<GenericPoll>(`/api/polls/${id}/vote`, { optionId }, voteHeaders()),
  closePoll: (id: string) => post<GenericPoll>(`/api/polls/${id}/close`, {}, voteHeaders()),
  forecast: (id: string) => getForecastReady(id),
  forecastPoll: (id: string) => forecastPollOnce(id),
  articles: (id: string) => get<Record<string, CampaignArticle[]>>(`/api/elections/${id}/articles`),
  leaderboard: () => get<Leaderboard>("/api/leaderboard"),
  vote: (id: string, candidateId: string, nickname?: string) =>
    post<PollTally>(`/api/elections/${id}/poll`, { candidateId, nickname }, voteHeaders()),
  // Crowd tally + this device's own pick (`myVote`) — used to restore the
  // highlight on load, since the cached forecast omits per-viewer data.
  electionPoll: (id: string) => request<PollTally>(`/api/elections/${id}/poll`, { headers: voteHeaders() }),

  // --- Candidates directory + profile resolution ---
  candidates: (country?: string | null) =>
    get<Candidacy[]>(`/api/candidates${country ? `?country=${country}` : ""}`),
  // Canonical /u/:username page: resolve + full detail in one round trip.
  // Same token semantics as `candidacy` (server flags `mine`).
  candidacyByUsername: (username: string) =>
    request<CandidacyDetail>(`/api/candidates/u/${encodeURIComponent(username)}/detail`, {
      headers: { ...adminAuth.header(), ...voteHeaders() },
    }),
  // /u/:username profile: attributes + election history + candidacies in one
  // round trip. Token/admin headers let the server include the viewer's own
  // pending rows (same semantics as the candidacy detail).
  userProfile: (username: string) =>
    request<UserProfile>(`/api/users/${encodeURIComponent(username)}/profile`, {
      headers: { ...adminAuth.header(), ...voteHeaders() },
    }),

  // --- Candidacies ---
  raceCandidacies: (countryId: string, office: CandidacyOffice) =>
    get<RaceCandidacies>(`/api/candidacy/race/${countryId}/${office}`),
  declareCandidacy: (countryId: string, office: CandidacyOffice, body: DeclareBody) =>
    post<CandidacyCreated>(`/api/candidacy/race/${countryId}/${office}`, body),
  // Send the per-user token so the server can flag `mine` (owner-only edit/answer).
  candidacy: (id: string) =>
    request<CandidacyDetail>(`/api/candidacy/${id}`, { headers: { ...adminAuth.header(), ...voteHeaders() } }),
  verifyToken: (id: string, token: string) =>
    post<{ valid: boolean }>(`/api/candidacy/${id}/verify`, {}, { "x-edit-token": token }),
  // Self-service ownership proof: start issues a nonce to set as a company name;
  // confirm checks it and returns a per-user edit token.
  startCompanyVerify: (id: string) =>
    post<{ nonce: string; expiresAt: number; instructions: string }>(`/api/candidacy/${id}/verify-company/start`, {}),
  confirmCompanyVerify: (id: string) =>
    post<{ editToken: string }>(`/api/candidacy/${id}/verify-company/confirm`, {}),

  // Candidacy-independent verification: any player proves ownership of a WarEra
  // account (same company-rename proof) to mint a per-user token — used to vote
  // on verified-only / min-level polls. `confirm` returns the token to store.
  startUserVerify: (username: string) =>
    post<{ nonce: string; expiresAt: number; instructions: string }>(`/api/verify/start`, { username }),
  confirmUserVerify: (username: string) => post<{ editToken: string }>(`/api/verify/confirm`, { username }),
  updatePlatform: (id: string, token: string, body: PlatformBody) =>
    request<CandidacyDetail>(`/api/candidacy/${id}`, { method: "PATCH", body, headers: { "x-edit-token": token } }),
  askQuestion: (id: string, bodyDoc: string, name?: string) =>
    post<AmaQuestion>(`/api/candidacy/${id}/questions`, { bodyDoc, name }),
  upvoteQuestion: (id: string, qid: string) =>
    post<{ upvotes: number }>(`/api/candidacy/${id}/questions/${qid}/upvote`, {}),
  answerQuestion: (id: string, token: string, questionId: string, answerDoc: string) =>
    post<{ ok: true }>(`/api/candidacy/${id}/answer`, { questionId, answerDoc }, { "x-edit-token": token }),

  // --- Admin (auth via the centralized adminAuth credential) ---
  adminCandidacies: () => request<Candidacy[]>(`/api/admin/candidacies`, { headers: adminAuth.header() }),
  adminUpdate: (id: string, patch: { review?: CandidacyReview; origin?: CandidacyOrigin }) =>
    request<{ ok: true }>(`/api/admin/candidacies/${id}`, { method: "PATCH", body: patch, headers: adminAuth.header() }),
  adminHideQuestion: (qid: string, hidden: boolean) =>
    request<{ ok: true }>(`/api/admin/questions/${qid}`, { method: "PATCH", body: { hidden }, headers: adminAuth.header() }),
  adminPolls: () => request<(GenericPoll & { review: PollReview })[]>(`/api/admin/polls`, { headers: adminAuth.header() }),
  adminSetPollReview: (id: string, review: PollReview) =>
    request<{ ok: true }>(`/api/admin/polls/${id}`, { method: "PATCH", body: { review }, headers: adminAuth.header() }),
  adminResetToken: (id: string) =>
    request<{ editToken: string }>(`/api/admin/candidacies/${id}/reset-token`, { method: "POST", headers: adminAuth.header() }),
  adminUsers: () => request<AdminUser[]>(`/api/admin/users`, { headers: adminAuth.header() }),
  // Verify mints + returns a fresh edit token; unverify revokes (returns { ok }).
  adminVerifyUser: (id: string) =>
    request<{ editToken: string }>(`/api/admin/users/${id}`, { method: "PATCH", body: { verified: true }, headers: adminAuth.header() }),
  adminUnverifyUser: (id: string) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: "PATCH", body: { verified: false }, headers: adminAuth.header() }),
  adminQuestions: () => request<AdminQuestion[]>(`/api/admin/questions`, { headers: adminAuth.header() }),
  adminUpdatePlatform: (id: string, body: { introDoc?: string; party?: string; measures?: { title: string; detailDoc?: string | null }[] }) =>
    request<CandidacyDetail>(`/api/admin/candidacies/${id}/platform`, { method: "PATCH", body, headers: adminAuth.header() }),
  adminAudit: () => request<AuditEntry[]>(`/api/admin/audit`, { headers: adminAuth.header() }),
};
